/**
 * Projects Pinia Store
 *
 * Orchestrates MVI: intents <-> reducer <-> effects.
 */

import { ref, computed } from "vue";
import { defineStore } from "pinia";
import type { ICreateProjectRequest } from "@/shared/api";
import type { IProjectsState } from "./state";
import {
  createInitialState,
  selectProjects,
  selectSelectedProjectId,
  selectIsLoading,
  selectIsCreating,
  selectError,
} from "./state";
import type { TProjectsIntent } from "./intents";
import { intents } from "./intents";
import { projectsReducer } from "./reducer";
import * as effects from "./effects";

export const useProjectsStore = defineStore("projects", () => {
  // ========================================================================
  // State
  // ========================================================================

  const state = ref<IProjectsState>(createInitialState());

  // ========================================================================
  // Dispatch
  // ========================================================================

  function dispatch(intent: TProjectsIntent): void {
    state.value = projectsReducer(state.value, intent);
  }

  // ========================================================================
  // Computed (Selectors)
  // ========================================================================

  const projects = computed(() => selectProjects(state.value));
  const selectedProjectId = computed(() => selectSelectedProjectId(state.value));
  const isLoading = computed(() => selectIsLoading(state.value));
  const isCreating = computed(() => selectIsCreating(state.value));
  const error = computed(() => selectError(state.value));

  // ========================================================================
  // Actions
  // ========================================================================

  async function loadProjects(): Promise<void> {
    await effects.loadProjects(dispatch);
  }

  function selectProject(projectId: string): void {
    dispatch(intents.selectProject(projectId));
  }

  async function createProject(request: ICreateProjectRequest): Promise<void> {
    await effects.createProject(dispatch, request);
  }

  // ========================================================================
  // Return
  // ========================================================================

  return {
    // State (readonly)
    state: computed(() => state.value),

    // Computed
    projects,
    selectedProjectId,
    isLoading,
    isCreating,
    error,

    // Actions
    loadProjects,
    selectProject,
    createProject,
  };
});
